import React from 'react';
import PropTypes from 'prop-types';
import { Icon, IconButton } from '@cbrebuild/blocks';
import VoucherFileUpload from './file-upload-button';
import VoucherFileListItem from './voucher-file-list-item';
import LoadMore from '../../../nucleus/load-more/load-more';
import { getAttachmentsExtract, subscribeToDealExtractionStatus } from '../../../services/attachments-service';

const BannerSparkleGlyph = () => (
  <svg className="voucher-import-banner__sparkle" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M8 2l1.4 3.6L13 7l-3.6 1.4L8 12 6.6 8.4 3 7l3.6-1.4L8 2z" fill="currentColor" />
    <path d="M17 3l0.8 2.1L20 6l-2.2 0.9L17 9l-0.8-2.1L14 6l2.2-0.9L17 3z" fill="currentColor" />
    <path d="M16 11l1 2.6L19.6 15 17 16l-1 2.6L15 16l-2.6-1 2.6-1.4L16 11z" fill="currentColor" />
  </svg>
);

class VoucherFiles extends React.Component {
  bannerStatusUnsubscribe = null;

  bannerStatusSubscriptionKey = null;

  state = {
    voucherFilesQueryParams: {
      ordering: '-modified',
      page: 1,
      page_size: 25,
    },
    dealOnlyFilesQueryParams: {
      ordering: '-modified',
      page: 1,
      page_size: 25,
    },
    isAddButton: true,
    showImportBanner: true,
    hasStoppedExtraction: false,
  }

  componentDidMount() {
    const { voucherFilesQueryParams, dealOnlyFilesQueryParams } = this.state;
    const { dealId, fetchVoucherFiles, fetchDealOnlyFiles } = this.props;
    fetchVoucherFiles({ ...voucherFilesQueryParams, deal: dealId });
    fetchDealOnlyFiles({ ...dealOnlyFilesQueryParams, deal: dealId });
    this.loadBannerExtractionStatus();
    this.subscribeToBannerExtractionStatus();
  }

  componentDidUpdate(prevProps) {
    const shouldRefreshBannerStatus =
      prevProps.dealId !== this.props.dealId
      || prevProps.showImportBanner !== this.props.showImportBanner
      || prevProps.dealType !== this.props.dealType;

    if (shouldRefreshBannerStatus) {
      this.loadBannerExtractionStatus();
    }

    const prevListenerFileId = this.getBannerListenerFileId(prevProps);
    const nextListenerFileId = this.getBannerListenerFileId(this.props);
    const dealChanged = prevProps.dealId !== this.props.dealId;

    if (dealChanged || prevListenerFileId !== nextListenerFileId) {
      this.subscribeToBannerExtractionStatus();
    }
  }

  componentWillUnmount() {
    this.unsubscribeFromBannerExtractionStatus();
  }

  getBannerListenerFileId = (props = this.props) => {
    const files = Array.isArray(props.voucherFiles)
      ? props.voucherFiles
      : (props.files || []).filter(file => file.is_lease_document);
    const firstLeaseFile = files.find(file => file && file.id);
    return firstLeaseFile ? String(firstLeaseFile.id) : null;
  }

  unsubscribeFromBannerExtractionStatus = () => {
    if (typeof this.bannerStatusUnsubscribe === 'function') {
      this.bannerStatusUnsubscribe();
    }
    this.bannerStatusUnsubscribe = null;
    this.bannerStatusSubscriptionKey = null;
  }

  subscribeToBannerExtractionStatus = () => {
    const { dealId, dealType, showImportBanner } = this.props;

    if (!showImportBanner || dealType !== 'lease' || !dealId) {
      this.unsubscribeFromBannerExtractionStatus();
      return;
    }

    const listenerFileId = this.getBannerListenerFileId();
    if (!listenerFileId) {
      this.unsubscribeFromBannerExtractionStatus();
      return;
    }

    const subscriptionKey = `${dealId}:${listenerFileId}`;
    if (this.bannerStatusSubscriptionKey === subscriptionKey && this.bannerStatusUnsubscribe) {
      return;
    }

    this.unsubscribeFromBannerExtractionStatus();

    this.bannerStatusUnsubscribe = subscribeToDealExtractionStatus({
      dealId,
      fileId: listenerFileId,
      onUpdate: (result, allResults) => {
        const results = Array.isArray(allResults)
          ? allResults
          : (result ? [result] : []);
        const hasStoppedExtraction = results.some(item => String(item?.status || '').toLowerCase() === 'stopped');
        this.setState({ hasStoppedExtraction });
      },
      onError: () => {
        this.setState({ hasStoppedExtraction: false });
      },
    });

    this.bannerStatusSubscriptionKey = subscriptionKey;
  }

  getExtractionResults = (payload) => {
    if (Array.isArray(payload)) {
      return payload;
    }

    if (Array.isArray(payload?.results)) {
      return payload.results;
    }

    if (Array.isArray(payload?.result)) {
      return payload.result;
    }

    return [];
  }

  loadBannerExtractionStatus = () => {
    const { dealId, dealType, showImportBanner } = this.props;

    if (!showImportBanner || dealType !== 'lease' || !dealId) {
      this.setState({ hasStoppedExtraction: false });
      return;
    }

    getAttachmentsExtract({ deal: dealId })
      .then((payload) => {
        const results = this.getExtractionResults(payload);
        const hasStoppedExtraction = results.some(item => String(item?.status || '').toLowerCase() === 'stopped');
        this.setState({ hasStoppedExtraction });
      })
      .catch(() => {
        this.setState({ hasStoppedExtraction: false });
      });
  }

  handleVoucherFilesLoadMore = () => {
    const { dealId, fetchVoucherFiles } = this.props;
    this.setState(({ voucherFilesQueryParams }) => ({
      voucherFilesQueryParams: {
        ...voucherFilesQueryParams,
        page: voucherFilesQueryParams.page + 1,
      },
    }), () => fetchVoucherFiles({ ...this.state.voucherFilesQueryParams, deal: dealId }));
  }

  handleDealOnlyFilesLoadMore = () => {
    const { dealId, fetchDealOnlyFiles } = this.props;
    this.setState(({ dealOnlyFilesQueryParams }) => ({
      dealOnlyFilesQueryParams: {
        ...dealOnlyFilesQueryParams,
        page: dealOnlyFilesQueryParams.page + 1,
      },
    }), () => fetchDealOnlyFiles({ ...this.state.dealOnlyFilesQueryParams, deal: dealId }));
  }

  dismissImportBanner = () => {
    this.setState({ showImportBanner: false });
  }

  // Moving a file between the voucher and deal-only sections (via the
  // Add to Voucher / Remove from Voucher actions) changes which section's
  // total count and visible page the file belongs to. The reducer alone
  // can't reliably keep the visible list topped back up to a full page of
  // 25 (it doesn't have the "next" hidden file to backfill with), so
  // re-fetch page 1 of both sections from the server after a move to keep
  // the visible lists, counts, and Load More amounts in sync with reality.
  handleUpdateFile = (id, params) => {
    const {
      dealId, updateFile, fetchVoucherFiles, fetchDealOnlyFiles,
    } = this.props;
    const isSectionMove = typeof params.is_lease_document === 'boolean';

    return Promise.resolve(updateFile(id, params)).then((result) => {
      if (isSectionMove) {
        const resetQueryParams = { ordering: '-modified', page: 1, page_size: 25 };
        this.setState({
          voucherFilesQueryParams: resetQueryParams,
          dealOnlyFilesQueryParams: resetQueryParams,
        });
        fetchVoucherFiles({ ...resetQueryParams, deal: dealId });
        fetchDealOnlyFiles({ ...resetQueryParams, deal: dealId });
      }
      return result;
    });
  }

  render() {
    const {
      dealId,
      dealType,
      isEnhancedFlow,
      deleteFile,
      fetchFilesDownloadLink,
      files,
      isLoadingFiles,
      isLoadingVoucherFiles,
      isLoadingDealOnlyFiles,
      uploadFile,
      userData,
      voucher,
      voucherFiles,
      commissionAgreementFiles,
      commissionAgreementFileIds,
      commissionAgreementDealOnlyCount,
      dealOnlyFiles,
      voucherFilesCount,
      dealOnlyFilesCount,
      voucherFilesNext,
      dealOnlyFilesNext,
      showImportBanner,
      showGettingStartedLayout,
      showAttachFilesIntro,
      onVoucherFileRemoved,
    } = this.props;

    const leaseFiles = voucherFiles || files.filter(file => file.is_lease_document);
    const leaseFileIds = new Set(leaseFiles.map(file => String(file.id)));
    const voucherSectionFiles = [
      ...leaseFiles,
      ...(commissionAgreementFiles || []).filter(file => !leaseFileIds.has(String(file.id))),
    ];
    const commissionAgreementFileIdSet = new Set(
      (commissionAgreementFileIds || []).map(id => String(id))
    );
    const nonLeaseFiles = (dealOnlyFiles || files.filter(file => file.is_lease_document === false))
      .filter(file => !commissionAgreementFileIdSet.has(String(file.id)));
    const remainingVoucherFilesCount = voucherFilesCount - leaseFiles.length;
    const visibleDealOnlyFilesCount = Math.max(0, dealOnlyFilesCount - commissionAgreementDealOnlyCount);
    const remainingDealOnlyFilesCount = Math.max(0, visibleDealOnlyFilesCount - nonLeaseFiles.length);
    const displayImportBanner = showImportBanner
      && this.state.showImportBanner
      && dealType === 'lease'
      && this.state.hasStoppedExtraction
      && leaseFiles.length > 0;

    return (
      <div className={`voucher-files-section${showGettingStartedLayout ? ' voucher-files-section--getting-started' : ''}`}>
        {displayImportBanner && (
          <div className="voucher-import-banner" role="status" aria-live="polite">
            <div className="voucher-import-banner__content">
              <Icon iconName="info-circle" />
              <div className="voucher-import-banner__text">
                <strong>New - Import data from a document to fill this voucher.</strong>
                <p>Find the lease below and select its <BannerSparkleGlyph /> icon to import the data into your voucher.</p>
              </div>
            </div>
            <IconButton
              className="voucher-import-banner__dismiss"
              iconName="close"
              variant="basic"
              onClick={this.dismissImportBanner}
            />
          </div>
        )}
        {showAttachFilesIntro && (
          <React.Fragment>
            <legend className="begin-voucher">Attach Files to Voucher (Sent along with Voucher)</legend>
            <p>
              {dealType === 'lease' || dealType === 'sale'
                ? `Required with this voucher: ${dealType.charAt(0).toUpperCase() + dealType.slice(1)} Agreement and Commission Agreement.`
                : 'Attach any related documents for this voucher.'}
            </p>
          </React.Fragment>
        )}
        <div className="files-tab voucher-file-section-list">
          <ul className="download-list">
            {voucherSectionFiles.map(file => (
              <VoucherFileListItem
                deleteFile={deleteFile}
                dealType={dealType}
                isEnhancedFlow={isEnhancedFlow}
                fetchFilesDownloadLink={fetchFilesDownloadLink}
                file={file}
                key={file.id}
                updateFile={this.handleUpdateFile}
                onRemoveFromVoucher={onVoucherFileRemoved}
                userData={userData}
                voucher={voucher}
                showSparkleIcon={showGettingStartedLayout}
                isAddButton={false}
              />
            ))}
          </ul>
          {remainingVoucherFilesCount > 0 &&
          <LoadMore
            isLoading={isLoadingVoucherFiles}
            handleLoadMore={this.handleVoucherFilesLoadMore}
            hasNextPage={voucherFilesNext || remainingVoucherFilesCount > 0}
            label={`Load More (${remainingVoucherFilesCount})`}
          />
          }
        </div>
        <div className="col-two-thirds">
          <VoucherFileUpload
            dealId={dealId}
            deleteFile={deleteFile}
            fetchFilesDownloadLink={fetchFilesDownloadLink}
            isLoadingFiles={isLoadingFiles}
            uploadFile={uploadFile}
          />
          {(nonLeaseFiles.length > 0 || dealOnlyFilesCount > 0)
            && <legend className="begin-voucher">Add Files from Deal (Not sent along with Voucher)</legend>}
          <ul className="download-list">
            {nonLeaseFiles.map(file => (
              <VoucherFileListItem
                deleteFile={deleteFile}
                dealType={dealType}
                isEnhancedFlow={isEnhancedFlow}
                fetchFilesDownloadLink={fetchFilesDownloadLink}
                file={file}
                key={file.id}
                updateFile={this.handleUpdateFile}
                userData={userData}
                voucher={voucher}
                showSparkleIcon={showGettingStartedLayout}
                isAddButton={this.state.isAddButton}
              />
              ))
            }
          </ul>
          {remainingDealOnlyFilesCount > 0 &&
          <LoadMore
            isLoading={isLoadingDealOnlyFiles}
            handleLoadMore={this.handleDealOnlyFilesLoadMore}
            hasNextPage={dealOnlyFilesNext || remainingDealOnlyFilesCount > 0}
            label={`Load More (${remainingDealOnlyFilesCount})`}
          />
          }
        </div>
      </div>
    );
  }
}

VoucherFiles.propTypes = {
  dealId: PropTypes.number.isRequired,
  dealType: PropTypes.string,
  isEnhancedFlow: PropTypes.bool,
  deleteFile: PropTypes.func.isRequired,
  fetchVoucherFiles: PropTypes.func.isRequired,
  fetchDealOnlyFiles: PropTypes.func.isRequired,
  fetchFilesDownloadLink: PropTypes.func.isRequired,
  voucher: PropTypes.shape({ subProp: PropTypes.string }).isRequired,
  files: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    deal: PropTypes.number,
    document_name: PropTypes.string,
    title: PropTypes.string,
    file_type: PropTypes.string,
    file_size: PropTypes.number,
    created_by: PropTypes.shape({
      id: PropTypes.number,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      avatar: PropTypes.string,
      email: PropTypes.shape({
        work: PropTypes.string,
      }),
    }),
    created: PropTypes.string,
    modified: PropTypes.string,
    can_edit: PropTypes.bool,
    privacy: PropTypes.string,
    is_lease_document: PropTypes.bool,
  })).isRequired,
  voucherFiles: PropTypes.arrayOf(PropTypes.shape({})),
  commissionAgreementFiles: PropTypes.arrayOf(PropTypes.shape({})),
  commissionAgreementFileIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  commissionAgreementDealOnlyCount: PropTypes.number,
  dealOnlyFiles: PropTypes.arrayOf(PropTypes.shape({})),
  voucherFilesCount: PropTypes.number,
  dealOnlyFilesCount: PropTypes.number,
  isLoadingFiles: PropTypes.bool,
  isLoadingVoucherFiles: PropTypes.bool,
  isLoadingDealOnlyFiles: PropTypes.bool,
  voucherFilesNext: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  dealOnlyFilesNext: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  updateFile: PropTypes.func,
  uploadFile: PropTypes.func.isRequired,
  userData: PropTypes.shape({}).isRequired,
  showImportBanner: PropTypes.bool,
  showGettingStartedLayout: PropTypes.bool,
  showAttachFilesIntro: PropTypes.bool,
  onVoucherFileRemoved: PropTypes.func,
};

VoucherFiles.defaultProps = {
  dealType: '',
  isEnhancedFlow: false,
  isLoadingFiles: false,
  isLoadingVoucherFiles: false,
  isLoadingDealOnlyFiles: false,
  voucherFiles: null,
  commissionAgreementFiles: [],
  commissionAgreementFileIds: [],
  commissionAgreementDealOnlyCount: 0,
  dealOnlyFiles: null,
  voucherFilesCount: 0,
  dealOnlyFilesCount: 0,
  voucherFilesNext: null,
  dealOnlyFilesNext: null,
  updateFile: () => {},
  showImportBanner: false,
  showGettingStartedLayout: false,
  showAttachFilesIntro: false,
  onVoucherFileRemoved: null,
};

export default VoucherFiles;
